'use client'

// NFC Error Logging Utility
// Provides comprehensive logging for NFC-related errors and diagnostics

export interface NFCErrorDetails {
    error: Error | DOMException;
    timestamp: Date;
    userAgent: string;
    isSecureContext: boolean;
    nfcSupported: boolean;
    permissions?: PermissionState;
    context?: string;
}

export interface NFCPermissionDetails {
    state: PermissionState;
    timestamp: Date;
    userAgent: string;
    context?: string;
}

class NFCLogger {

    /**
     * Log an NFC error with comprehensive diagnostics
     */
    logError(error: Error | DOMException, context?: string): void {
        const errorDetails: NFCErrorDetails = {
            error,
            timestamp: new Date(),
            userAgent: navigator.userAgent,
            isSecureContext: window.isSecureContext,
            nfcSupported: 'NDEFReader' in window,
            context,
        };

        // Log to console with detailed information
        console.error('NFC Error:', {
            name: error.name,
            message: error.message,
            context,
            userAgent: navigator.userAgent,
            isSecureContext: window.isSecureContext,
            nfcSupported: 'NDEFReader' in window,
            timestamp: new Date().toISOString(),
            ...(error instanceof DOMException && { code: error.code }),
        });

        // Check for specific error patterns and provide additional diagnostics
        this.diagnoseError(error, context);
    }

    /**
     * Log NFC permission state changes
     */
    async logPermissionState(context?: string): Promise<void> {
        try {
            const permission = await navigator.permissions.query({ name: 'nfc' as PermissionName });

            const permissionDetails: NFCPermissionDetails = {
                state: permission.state,
                timestamp: new Date(),
                userAgent: navigator.userAgent,
                context,
            };

            console.log('NFC Permission State:', permissionDetails);

        } catch (error) {
            console.error('Failed to check NFC permissions:', error);
        }
    }

    /**
     * Log successful NFC operations
     */
    logSuccess(operation: string, details?: any): void {
        console.log(`NFC Success: ${operation}`, {
            operation,
            timestamp: new Date().toISOString(),
            details,
        });
    }

    /**
     * Log NFC operation attempts
     */
    logOperation(operation: string, details?: any): void {
        console.log(`NFC Operation: ${operation}`, {
            operation,
            timestamp: new Date().toISOString(),
            details,
        });
    }

    private diagnoseError(error: Error | DOMException, context?: string): void {
        if (error instanceof DOMException) {
            switch (error.name) {
                case 'AbortError':
                    console.warn('NFC Diagnosis: Operation was aborted. This may happen if the user cancels or another scan starts.');
                    break;
                case 'InvalidStateError':
                    console.warn('NFC Diagnosis: Invalid state. Another NFC operation may already be in progress.');
                    break;
                case 'NotAllowedError':
                    console.warn('NFC Diagnosis: Permission denied. Check browser settings and ensure user has granted NFC permission.');
                    break;
                case 'NotSupportedError':
                    console.warn('NFC Diagnosis: NFC not supported. Ensure using Chrome on Android with NFC hardware.');
                    break;
                case 'NotReadableError':
                    console.warn('NFC Diagnosis: NFC hardware not accessible. Check device NFC settings and hardware.');
                    break;
                case 'SecurityError':
                    console.warn('NFC Diagnosis: Security error. Ensure HTTPS context and proper permissions.');
                    break;
                case 'NetworkError':
                    console.warn('NFC Diagnosis: Network error during NFC operation.');
                    break;
                default:
                    console.warn(`NFC Diagnosis: Unhandled DOMException: ${error.name} - ${error.message}`);
            }
        } else {
            console.warn(`NFC Diagnosis: JavaScript Error: ${error.name} - ${error.message}`);
        }

        // Additional context-specific diagnostics
        if (context) {
            console.log(`NFC Context: Error occurred during ${context}`);
        }
    }
}

// Export singleton instance
export const nfcLogger = new NFCLogger();

export default nfcLogger;